import { motion, AnimatePresence } from 'framer-motion';
import { CSSProperties, useContext, useEffect, useMemo } from 'react';
import { KlartextEventConfigContext } from '../providers/klartextEventConfig';
import { useKpiCollection } from '../hooks/useKpiCollection';
import ListeningView from './ListeningView';
import { useGlobalState } from '../providers/globalState';
import { AttacksKpiPoint, AttacksValuePoint } from '@configTypes/kpiAttacks';

interface SpeakerAttack extends AttacksValuePoint {
  index: number;
}

export const AttacksView = () => {
  const speechConfig = useContext(KlartextEventConfigContext);
  const globalState = useGlobalState();
  const { dispatch } = globalState ?? {};

  const [attacksKpis, loading] = useKpiCollection<AttacksKpiPoint[]>({
    eventId: speechConfig?.eventId ?? null,
    kpiName: 'Attacks',
    initialState: [] as AttacksKpiPoint[],
  });

  const attacks = useMemo(() => {
    const candidateIndices: { [speaker: string]: number } = {};

    return attacksKpis
      .reduce<SpeakerAttack[]>((acc, kpi) => {
        const newAttacks = kpi.value
          .filter(
            attack =>
              attack.speaker === speechConfig?.frontendSpecificConfig?.participants.democrat.name ||
              attack.speaker === speechConfig?.frontendSpecificConfig?.participants.republican.name,
          )
          .map(attack => {
            if (!candidateIndices[attack.speaker]) {
              candidateIndices[attack.speaker] = 0;
            }
            candidateIndices[attack.speaker]++;

            return {
              index: candidateIndices[attack.speaker],
              speaker: attack.speaker,
              attackQuote: attack.attackQuote,
              reasoning: attack.reasoning,
              issueFocusIndex: attack.issueFocusIndex,
              issueFocusIndexReasoning: attack.issueFocusIndexReasoning,
              civilityIndex: attack.civilityIndex,
              civilityIndexReasoning: attack.civilityIndexReasoning,
              attractedVoterGroups: attack.attractedVoterGroups,
              attractedVoterGroupsReasoning: attack.attractedVoterGroupsReasoning,
              rhetoricalDevice: attack.rhetoricalDevice,
              rhetoricalDeviceReasoning: attack.rhetoricalDeviceReasoning,
            };
          });

        return [...acc, ...newAttacks];
      }, [])
      .reverse();
  }, [attacksKpis]);

  if (attacksKpis.length === 0 || loading) {
    return <ListeningView />;
  }

  return (
    <AnimatePresence>
      <div className="mt-4 flex w-full flex-col items-center gap-y-4">
        {attacks.map((attack, i) => (
          <motion.div
            layout
            key={`promise-${attack.speaker}-${attack.index}`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
            className="w-[95%]">
            <div
              className={`relative rounded-[1.5rem] p-4 ${
                attack.speaker === speechConfig?.frontendSpecificConfig?.participants.republican.name
                  ? 'bg-republican'
                  : 'bg-democrat'
              } text-partyColor shadow-lg`}
              style={{
                transform: `rotate(${i % 2 === 0 ? -2 : 2}deg)`,
              }}>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-bold">{attack.rhetoricalDevice}</div>
                <div className="font-normal text-sm">{`Attack #${attack.index} by ${attack.speaker}`}</div>
              </div>
              <div className="rounded-[1rem] bg-white p-4 text-eidraBlack">
                <span className="mb-2 block text-xl font-medium leading-tight">&quot;{attack.attackQuote}&quot;</span>
                <span className="text-sm">{attack.reasoning}</span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {attack.attractedVoterGroups?.map(group => <Chip key={group} label={group} />)}
                </div>
              </div>
              <div
                className={`absolute h-5 w-5 ${
                  attack.speaker === speechConfig?.frontendSpecificConfig?.participants.republican.name
                    ? 'bg-republican'
                    : 'bg-democrat'
                } bottom-[-0.5rem] rotate-45 transform ${i % 2 === 0 ? 'right-8' : 'left-8'}`}
              />
            </div>
          </motion.div>
        ))}
      </div>
    </AnimatePresence>
  );
};

const Chip = ({ label }: { label: string }) => {
  return (
    <div className="rounded-full border border-eidraBlack bg-eidraGrey px-2 py-1 text-[0.625rem] font-bold leading-none">
      <span className="mr-1">👍</span>
      {label}
    </div>
  );
};
